<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import AdjustSheet from './components/AdjustSheet.vue'
import AnnotationDialog from './components/AnnotationDialog.vue'
import AnnotationExitDialog from './components/AnnotationExitDialog.vue'
import AnnotationMediaPreview from './components/AnnotationMediaPreview.vue'
import AnnotationOverlay from './components/AnnotationOverlay.vue'
import AiInsightSheet from './components/AiInsightSheet.vue'
import CoachTour from './components/CoachTour.vue'
import InfoSheet from './components/InfoSheet.vue'
import LoadingState from './components/LoadingState.vue'
import LoginPromptDialog from './components/LoginPromptDialog.vue'
import ProjectResultPanel from './components/ProjectResultPanel.vue'
import SnapshotDialog from './components/SnapshotDialog.vue'
import ToolRail from './components/ToolRail.vue'
import VideoRecordingPanel from './components/VideoRecordingPanel.vue'
import ViewerIcon from './components/ViewerIcon.vue'
import VirtualJoysticks from './components/VirtualJoysticks.vue'
import { ViewerRuntime } from './viewer-runtime.js'
import { readViewerConfig } from './viewer-config.js'
import { AiChatApi, ModelGuideApi, createAiGuideFeedbackRequestId } from './api.js'
import { getServicePausedMessage } from './service-messages.js'
import companyAvatar from './assets/company-avatar.png'
import { attachSoftwareLandscapeSheetScroll } from './sheet-scroll.js'

const config = readViewerConfig()
const VIDEO_RECORDING_ENTRY_VISIBLE = false
const viewerRoot = ref(null)
const canvas = ref(null)
const panel = ref('')
const railCollapsed = ref(false)
const cameraMode = ref('orbit')
const cameraTrajectoryAvailable = ref(false)
const cameraTrajectoryPlaying = ref(false)
const videoRecording = reactive({
  available: false,
  mode: '',
  status: 'idle',
  progress: 0,
  url: '',
  filename: '',
  saving: false,
  saveProgress: 0,
  savingAction: ''
})
const imageVideo = reactive({
  status: 'idle',
  taskStatus: 'idle',
  stageText: '',
  taskId: '',
  url: '',
  expiresAt: 0,
  saving: false,
  savingAction: '',
  error: '',
  retryable: true,
  resumeOnly: false,
  canPause: false
})
const entrancePlaying = ref(false)
const loaded = ref(false)
const tourOpen = ref(false)
const tourStepIndex = ref(0)
const landscapeMode = ref(false)
const physicalLandscape = ref(false)
const snapshotImage = ref('')
const aiStatus = ref('idle')
const aiIntroduction = ref('')
const aiError = ref('')
const aiGuideRevision = ref(null)
const aiFeedbackStatus = ref('idle')
const aiFeedbackError = ref('')
const aiInsightSeen = ref(false)
const annotations = ref([])
const annotationVisible = ref(true)
const annotationEditing = ref(false)
const annotationChanged = ref(false)
const annotationCurrentIndex = ref(0)
const annotationExpanded = ref(false)
const annotationCallout = ref(false)
const annotationMenu = ref(null)
const annotationDialogOpen = ref(false)
const annotationDialogMode = ref('view')
const annotationDialogItem = ref(null)
const pendingAnnotationPosition = ref(null)
const annotationExitOpen = ref(false)
const annotationSaving = ref(false)
const annotationSaveProgress = ref(null)
const annotationUploadItems = ref([])
const annotationSnapshotFlash = ref(false)
const annotationPreviewAttachment = ref(null)
const annotationPreviewKind = ref('')
const annotationPreviewLayout = ref(null)
const initialViewSaving = ref(false)
const toast = ref('')
const toastVisible = ref(false)
const infoAvatarFailed = ref(false)
const descriptionExpanded = ref(false)
const INITIAL_GESTURE_TOAST = '单指旋转 · 双指缩放/平移'
const likeLoginPromptOpen = ref(false)
const loginPromptKind = ref('like')
let toastTimer = 0
let controller = null
let detachSheetScroll = null
let enteredFullscreen = false
let orientationRequestToken = 0
let aiRequestController = null
let aiFeedbackRequestController = null
let aiFeedbackClientRequestId = ''
let aiFeedbackRequestSignature = ''
let aiAutoTimer = 0
let aiAutoRequested = false

const aiApi = new AiChatApi(config.apiBase, config.authToken, config.channel)
const modelGuideApi = new ModelGuideApi(config.apiBase, config.authToken, config.channel)
const aiBusy = computed(() => ['preparing', 'loading'].includes(aiStatus.value))
const aiNoticeVisible = computed(() => aiStatus.value === 'success' && !aiInsightSeen.value)
const gestureNoticeActive = computed(() => toastVisible.value && toast.value === INITIAL_GESTURE_TOAST)

function createAiIntroductionPrompt() {
  const title = String(meta.title || '3D 空间').trim()
  const description = String(meta.description || '暂无简介').trim()
  return [
    '请结合随附的当前模型多视角画面，用中文介绍这个地方。文字要有自然的人文气息和旅行叙事感，既准确又有画面感，避免流水账和空泛抒情。',
    `模型名称：${title}`,
    `已有简介：${description}`,
    '请使用 Markdown，并严格按以下四个二级标题组织回答。每个部分写 1—2 段自然段，内容可以有 3—5 句话，但不要拆成过多要点、列表或三级标题，不要重复同一内容，也不要增加其他栏目：',
    '## 地方介绍',
    '概括这里是什么地方、可确认的位置、整体环境和核心看点，并写出它给人的地方气质、生活感或人文意味。',
    '## 历史与文化',
    '概括历史沿革、文化背景和文化特色，适当说明它与地方生活、风俗记忆或文化传承的联系；如果有可靠依据，请直接给出明确的年代、人物和事件，不要回避具体信息。只有确实缺乏可靠依据时，才用一句话简短说明资料未明，不要反复声明或编造细节。',
    '## 主要景观',
    '介绍最值得关注的建筑、自然环境、空间关系或画面细节，并说明这些景观为什么值得看、能让游客感受到什么。',
    '## 游览建议',
    '给出实用而有温度的观察顺序、停留建议和体验方式，让游客知道如何慢下来感受这里，而不是只罗列打卡点。',
    '请只写与当前模型和画面有关的内容，信息不足时直接说明；不要介绍 3DGS 原理、重建参数或内部技术流程。'
  ].join('\n')
}

const fullscreenElement = () => document.fullscreenElement || document.webkitFullscreenElement || null
const isSoftwareLandscape = computed(() => landscapeMode.value && !physicalLandscape.value)

const loading = reactive({
  visible: !config.debugUi,
  error: false,
  title: '正在加载模型',
  text: '',
  kind: 'loading',
  progress: 0,
  indeterminate: true,
  loadedBytes: 0,
  totalBytes: 0,
  showProgressDetails: false,
  canRetry: false,
  canExit: false
})

const settings = reactive({
  fov: 75,
  speed: 24,
  background: '#000000',
  moveSpeed: 24,
  orbitSpeed: 24,
  autoRotateSpeed: 45,
  pinchSpeed: 2.4,
  orbitMotionType: 'orbit',
  motionData: null,
  initialCameraPose: null,
  skyboxEnabled: false,
  skyboxPreset: 'echopark'
})

const meta = reactive({
  taskId: config.taskId,
  title: config.fallbackTitle || '3D 空间',
  description: '',
  author: 'MeTop 用户',
  avatar: '',
  likes: 0,
  isLiked: false,
  views: 0,
  createdAt: '',
  pipeline: config.pipelineHint || '3dgs',
  modelFormat: config.requestedFormat || '',
  arProjectionAvailable: false
})

watch(() => meta.avatar, () => {
  infoAvatarFailed.value = false
})

watch(() => meta.description, () => {
  descriptionExpanded.value = false
})

const isProject = computed(() => config.viewMode === 'project')
const canEditAnnotations = computed(() => isProject.value)
const canProjectAr = computed(() => meta.arProjectionAvailable === true)
const showMiniProgramHome = computed(() => config.embedded && (
  (config.sharedEntry && !isProject.value) || config.entrySource === 'notification'
))
const useDualJoysticks = computed(() => landscapeMode.value || physicalLandscape.value)
const flyControlHint = computed(() => useDualJoysticks.value
  ? '左侧摇杆移动 · 右侧摇杆转动视角'
  : '左侧摇杆移动 · 手指滑动画面转动视角')
const hasPanel = computed(() => Boolean(panel.value))
const overlayVisible = computed(() => panel.value && panel.value !== 'result')
const annotationUiSuppressed = computed(() => (
  hasPanel.value ||
  likeLoginPromptOpen.value ||
  annotationDialogOpen.value ||
  annotationExitOpen.value ||
  Boolean(annotationPreviewAttachment.value) ||
  ['recording', 'processing'].includes(videoRecording.status) ||
  !['idle', 'ready'].includes(imageVideo.status)
))
const rootClasses = computed(() => ({
  'project-view': isProject.value,
  'has-panel': hasPanel.value,
  'annotation-ui-suppressed': annotationUiSuppressed.value,
  'entrance-playing': entrancePlaying.value,
  'tour-active': tourOpen.value,
  'tour-detail-active': tourOpen.value && tourStepIndex.value > 0,
  'landscape-mode': landscapeMode.value,
  'physical-landscape': physicalLandscape.value,
  'software-landscape': isSoftwareLandscape.value,
  'fly-mode': cameraMode.value === 'fly',
  'annotation-editing': annotationEditing.value,
  'video-recording-active': ['recording', 'processing'].includes(videoRecording.status) ||
    !['idle', 'ready'].includes(imageVideo.status)
}))

const tourSteps = computed(() => [
  cameraMode.value === 'fly'
    ? {
        canvasOverlay: true,
        demo: 'flight',
        title: useDualJoysticks.value ? '用双摇杆自由飞行' : '用单摇杆自由飞行',
        description: useDualJoysticks.value
          ? '左侧摇杆控制移动，右侧摇杆控制视角；使用摇杆时也可以同时拖动画面调整视角。'
          : '左侧摇杆控制移动；用手指在模型画面上滑动，就能转动视角。',
        icon: 'mode-flight',
        hints: [
          { side: '左侧摇杆', title: '控制移动', text: '前后移动 · 左右横移' },
          useDualJoysticks.value
            ? { side: '右侧摇杆', title: '控制视角', text: '左右转向 · 抬头低头' }
            : { side: '模型画面', title: '手指滑动', text: '转动视角 · 抬头低头' }
        ]
      }
    : {
        canvasOverlay: true,
        demo: 'orbit',
        title: '在画布上浏览模型',
        description: '这些手势直接作用于模型画面，可以围绕当前焦点观察空间。',
        icon: 'mode-orbit',
        hints: [
          { icon: 'hand-drag', title: '单指拖动', text: '旋转观察', motion: 'drag' },
          { icon: 'pinch', title: '双指捏合', text: '拉近 / 拉远', motion: 'pinch' },
          { icon: 'two-finger-pan', title: '双指拖动', text: '平移焦点', motion: 'pan' },
          { icon: 'double-tap', title: '双击模型', text: '快速聚焦', motion: 'double-tap' }
        ]
      },
  {
    target: '[data-tour="settings"]',
    title: '打开更多工具',
    description: [
      canEditAnnotations.value ? '模型标注' : '',
      '显示参数',
      cameraTrajectoryAvailable.value ? '轨迹播放' : '',
      VIDEO_RECORDING_ENTRY_VISIBLE && videoRecording.available ? '视频录制' : '',
      '操作教程'
    ].filter(Boolean).join('、') + '都集中在这里。',
    icon: 'more',
    placement: 'left'
  },
  {
    target: '[data-tour="like"]',
    title: '收藏喜欢的空间',
    description: '点击爱心为作品点赞，再次点击可以取消。登录后点赞状态会同步到社区。',
    icon: 'heart',
    placement: 'left'
  },
  canProjectAr.value
    ? {
        target: '[data-tour="ar"]',
        title: '投影到真实空间',
        description: '点击投影，可在微信小程序中将当前模型放到真实环境里查看。',
        icon: 'ar',
        placement: 'left'
      }
    : null,
  {
    target: '[data-tour="orientation"]',
    title: '切换横竖屏',
    description: landscapeMode.value
      ? '当前是横屏布局，点击这里可以返回竖屏浏览。'
      : '当前是竖屏布局，点击这里可以切换到视野更开阔的横屏浏览。',
    icon: landscapeMode.value ? 'screen-portrait' : 'screen-landscape',
    placement: 'left'
  },
  {
    target: '[data-tour="mode"]',
    title: '切换浏览模式',
    description: cameraMode.value === 'fly'
      ? '当前是飞行模式，点击这里可切换到围绕模型观察的轨道模式。'
      : `当前是轨道模式，点击这里可切换到飞行模式；${flyControlHint.value}。`,
    icon: cameraMode.value === 'fly' ? 'mode-flight' : 'mode-orbit',
    placement: 'left'
  },
  {
    target: '[data-tour="ai"]',
    title: '使用 AI 工具',
    description: '展开后可选择 AI 导游或 AI 视频：导游会结合模型画面提供讲解，AI 视频可将当前空间生成动态内容。',
    icon: 'ai',
    placement: 'left'
  },
  {
    target: '[data-tour="share"]',
    title: '分享当前模型',
    description: '分享的是当前模型入口，好友打开后会直接进入同一个模型页面。',
    icon: 'share',
    placement: 'left'
  }
].filter(Boolean))

function compactTitle(value, maxLength = 13) {
  const text = String(value || '').trim()
  return text.length > maxLength ? `${text.slice(0, maxLength)}…` : text
}

function updateLoading(patch) {
  Object.assign(loading, patch)
}

function notifyMiniProgramLikeChanged(nextMeta) {
  const miniProgram = window.wx?.miniProgram
  const taskId = String(nextMeta?.taskId || config.taskId || '').trim()
  const likes = Number(nextMeta?.likes)
  if (!config.embedded || !miniProgram?.postMessage || !taskId || !Number.isFinite(likes)) return
  miniProgram.postMessage({
    data: {
      type: 'taskLikeChanged',
      taskId,
      likes: Math.max(0, likes),
      isLiked: Boolean(nextMeta?.isLiked)
    }
  })
}

function updateMeta(nextMeta) {
  Object.assign(meta, nextMeta || {})
  if (nextMeta && ('likes' in nextMeta || 'isLiked' in nextMeta)) {
    notifyMiniProgramLikeChanged(meta)
  }
}

function updateSettings(nextSettings) {
  Object.assign(settings, nextSettings || {})
}

function showToast(message, duration) {
  window.clearTimeout(toastTimer)
  toast.value = String(message || '')
  toastVisible.value = Boolean(toast.value)
  const timeout = Number(duration) > 0 ? Number(duration) : (toast.value === INITIAL_GESTURE_TOAST ? 3800 : 2200)
  toastTimer = window.setTimeout(() => {
    toastVisible.value = false
  }, timeout)
}

function openLoginPrompt(kind = 'like') {
  loginPromptKind.value = kind
  likeLoginPromptOpen.value = true
}

function buildViewerReturnUrl() {
  const params = new URLSearchParams(window.location.search)
  const keys = [
    'taskId', 'title', 'imageUrl', 'format', 'pipeline', 'activity',
    'shareId', 'view', 'entry', 'shared', 'embedded', 'clientId', 'apiBase', 'videoSubscribeEnabled'
  ]
  const query = keys
    .filter(key => params.has(key))
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params.get(key) || '')}`)
  if (!params.has('shared')) query.push('shared=1')
  return query.length ? `/pages/webview/index?${query.join('&')}` : '/pages/webview/index'
}

function goToLogin() {
  likeLoginPromptOpen.value = false
  const miniProgram = window.wx?.miniProgram
  const returnUrl = buildViewerReturnUrl()
  const directLoginUrl = `/pages/projects/index?autoLogin=1&returnUrl=${encodeURIComponent(returnUrl)}`
  const fallbackUrl = '/pages/projects/index'
  if (miniProgram?.reLaunch) {
    miniProgram.reLaunch({
      url: directLoginUrl,
      fail: () => {
        if (miniProgram.navigateTo) {
          miniProgram.navigateTo({ url: directLoginUrl, fail: () => miniProgram.switchTab?.({ url: fallbackUrl }) })
        } else {
          miniProgram.switchTab?.({ url: fallbackUrl })
        }
      }
    })
    return
  }
  if (miniProgram?.navigateTo) {
    miniProgram.navigateTo({
      url: directLoginUrl,
      fail: () => miniProgram.switchTab?.({ url: fallbackUrl })
    })
    return
  }
  if (miniProgram?.switchTab) {
    miniProgram.switchTab({ url: fallbackUrl })
    return
  }
  showToast('请返回小程序完成登录')
}

function requestLike() {
  if (!config.authToken) {
    openLoginPrompt('like')
    return
  }
  controller?.toggleLike()
}

function goMiniProgramHome() {
  const miniProgram = window.wx?.miniProgram
  if (!miniProgram) {
    if (window.history.length > 1) window.history.back()
    return
  }
  const url = '/pages/community/index'
  const reLaunch = () => miniProgram.reLaunch?.({ url })
  if (miniProgram.switchTab) {
    miniProgram.switchTab({ url, fail: reLaunch })
    return
  }
  reLaunch()
}

function exitViewer() {
  const miniProgram = window.wx?.miniProgram
  if (config.embedded && miniProgram) {
    if (miniProgram.navigateBack) {
      miniProgram.navigateBack({ delta: 1, fail: goMiniProgramHome })
      return
    }
    goMiniProgramHome()
    return
  }
  if (window.history.length > 1) window.history.back()
  else window.location.assign('/')
}

function closePanel() {
  panel.value = ''
}

function openPanel(name) {
  if (annotationEditing.value && name) return
  tourOpen.value = false
  panel.value = name
}

function updateAnnotations(state = {}) {
  annotations.value = Array.isArray(state.annotations) ? state.annotations : annotations.value
  annotationVisible.value = state.visible !== false
  annotationEditing.value = Boolean(state.editing)
  annotationChanged.value = Boolean(state.changed)
  if (!annotations.value.length) annotationCurrentIndex.value = 0
  else annotationCurrentIndex.value = Math.min(annotationCurrentIndex.value, annotations.value.length - 1)
  if (typeof state.expanded === 'boolean') annotationExpanded.value = state.expanded
  if (typeof state.callout === 'boolean') annotationCallout.value = state.callout
}

function findAnnotation(id) {
  return annotations.value.find(item => item.id === id) || null
}

function beginAnnotationEdit() {
  if (!canEditAnnotations.value) return
  if (!requireLoaded()) return
  if (cameraMode.value === 'fly') exitFlyMode({ toast: false })
  closePanel()
  closeTour()
  railCollapsed.value = true
  annotationMenu.value = null
  annotationExpanded.value = false
  controller?.toggleAllAnnotationPanels(false)
  if (controller?.setAnnotationEditMode(true) !== false) {
    showToast('点击模型表面添加标注')
  }
}

function requestFinishAnnotationEdit() {
  annotationMenu.value = null
  if (!annotationChanged.value) {
    finishAnnotationEdit()
    return
  }
  annotationExitOpen.value = true
}

function finishAnnotationEdit() {
  annotationExitOpen.value = false
  controller?.setAnnotationEditMode(false)
  annotationMenu.value = null
  railCollapsed.value = false
}

function discardAnnotationEdit() {
  controller?.discardAnnotationChanges()
  finishAnnotationEdit()
  showToast('已放弃标注修改')
}

async function saveAnnotationEdit() {
  if (annotationSaving.value) return
  annotationSaving.value = true
  annotationSaveProgress.value = null
  annotationUploadItems.value = []
  try {
    const result = await controller?.saveAnnotations(progress => {
      annotationSaveProgress.value = progress
      if (progress?.phase !== 'attachment') return
      const key = `${progress.current || 0}:${progress.index || 0}:${progress.name || '附件'}`
      const item = {
        key,
        name: progress.name || '附件',
        progress: Math.max(0, Math.min(100, Math.round(Number(progress.progress) || 0))),
        status: progress.status || 'uploading',
        error: progress.error || ''
      }
      const index = annotationUploadItems.value.findIndex(value => value.key === key)
      if (index >= 0) annotationUploadItems.value[index] = item
      else annotationUploadItems.value.push(item)
    })
    finishAnnotationEdit()
    if (result?.skippedAttachmentCount) {
      showToast(`标注已保存，${result.skippedAttachmentCount} 个附件上传失败：${result.attachmentErrors?.[0] || '请重试'}`, 8000)
    } else {
      showToast('标注已保存')
    }
  } catch (error) {
    showToast(error?.message || '标注保存失败', 8000)
  } finally {
    annotationSaving.value = false
    annotationSaveProgress.value = null
    annotationUploadItems.value = []
  }
}

function openAnnotationDialog(mode, item = null) {
  annotationMenu.value = null
  annotationDialogMode.value = mode
  annotationDialogItem.value = item
  annotationDialogOpen.value = true
}

function closeAnnotationDialog() {
  annotationDialogOpen.value = false
  annotationDialogItem.value = null
  pendingAnnotationPosition.value = null
}

function handleAnnotationCreate(detail) {
  pendingAnnotationPosition.value = detail?.position || null
  openAnnotationDialog('create')
}

function handleAnnotationSelect(detail) {
  const item = findAnnotation(detail?.id)
  if (!item) return
  const index = annotations.value.findIndex(annotation => annotation.id === item.id)
  if (index >= 0) annotationCurrentIndex.value = index
  if (detail.editing) {
    annotationMenu.value = {
      id: item.id,
      x: Math.max(76, Math.min(window.innerWidth - 76, Number(detail.clientX) || window.innerWidth / 2)),
      y: Math.max(78, Math.min(window.innerHeight - 78, Number(detail.clientY) || window.innerHeight / 2))
    }
  }
}

function handleAnnotationMenuPosition(detail) {
  if (!annotationMenu.value || annotationMenu.value.id !== detail?.id) return
  annotationMenu.value = {
    ...annotationMenu.value,
    x: Math.max(76, Math.min(window.innerWidth - 76, Number(detail.clientX) || annotationMenu.value.x)),
    y: Math.max(78, Math.min(window.innerHeight - 78, Number(detail.clientY) || annotationMenu.value.y))
  }
}

function handleAnnotationAttachmentPreview(detail) {
  const kind = String(detail?.kind || '')
  const attachment = detail?.attachment
  if (!attachment?.url || !['image', 'video'].includes(kind)) return
  annotationPreviewAttachment.value = attachment
  annotationPreviewKind.value = kind
  annotationPreviewLayout.value = detail?.mediaLayout || null
}

function closeAnnotationAttachmentPreview() {
  annotationPreviewAttachment.value = null
  annotationPreviewKind.value = ''
  annotationPreviewLayout.value = null
}

function saveAnnotationDialog(data) {
  if (annotationDialogMode.value === 'create') {
    if (!pendingAnnotationPosition.value) {
      closeAnnotationDialog()
      return
    }
    controller?.createAnnotation(pendingAnnotationPosition.value, data)
  } else if (annotationDialogItem.value?.id) {
    controller?.updateAnnotation(annotationDialogItem.value.id, data)
  }
  closeAnnotationDialog()
}

function deleteDialogAnnotation() {
  const id = annotationDialogItem.value?.id
  if (id) controller?.deleteAnnotation(id)
  closeAnnotationDialog()
}

function editMenuAnnotation() {
  const item = findAnnotation(annotationMenu.value?.id)
  if (item) openAnnotationDialog('edit', item)
}

function snapshotMenuAnnotation() {
  const id = annotationMenu.value?.id
  if (id && controller?.updateAnnotationCameraPose(id)) {
    annotationSnapshotFlash.value = false
    requestAnimationFrame(() => {
      annotationSnapshotFlash.value = true
      window.setTimeout(() => { annotationSnapshotFlash.value = false }, 220)
    })
    showToast('标注视角已更新')
  }
  annotationMenu.value = null
}

function deleteMenuAnnotation() {
  const id = annotationMenu.value?.id
  if (id) controller?.deleteAnnotation(id)
  annotationMenu.value = null
}

function stepAnnotation(direction) {
  if (!annotations.value.length) return
  annotationCurrentIndex.value = (annotationCurrentIndex.value + direction + annotations.value.length) % annotations.value.length
  controller?.focusAnnotation(annotations.value[annotationCurrentIndex.value].id)
}

function toggleAnnotationVisible() {
  annotationVisible.value = controller?.toggleAnnotationVisibility() !== false
  if (!annotationVisible.value) annotationExpanded.value = false
}

function toggleAnnotationExpanded() {
  annotationExpanded.value = controller?.toggleAllAnnotationPanels(!annotationExpanded.value) || false
}

function toggleAnnotationStyle() {
  annotationCallout.value = controller?.toggleAnnotationStyle(!annotationCallout.value) || false
}

function updateRailCollapsed(collapsed) {
  if (!collapsed && panel.value && panel.value !== 'result') closePanel()
  railCollapsed.value = collapsed
}

function markAiInsightSeen() {
  if (aiInsightSeen.value) return
  aiInsightSeen.value = true
}

function readAiGuideRevision(value) {
  if (value === null || value === undefined || value === '') return null
  const revision = Number(value)
  return Number.isInteger(revision) && revision >= 0 ? revision : null
}

function resetAiGuideFeedback() {
  aiFeedbackRequestController?.abort()
  aiFeedbackRequestController = null
  aiFeedbackClientRequestId = ''
  aiFeedbackRequestSignature = ''
  aiFeedbackStatus.value = 'idle'
  aiFeedbackError.value = ''
}

function aiGuideFeedbackErrorMessage(error) {
  if (error?.code === 'AI_GUIDE_FEEDBACK_REVISION_STALE') return 'AI 导游内容已更新，请刷新页面后再反馈'
  if (error?.code === 'MODEL_GUIDE_CACHE_MISS') return '当前导游内容还未保存完成，请稍后再试'
  if (error?.code === 'MODEL_GUIDE_ACCESS_DENIED') return '当前模型暂时无法提交导游反馈'
  if (error?.code === 'AI_GUIDE_FEEDBACK_MIGRATION_REQUIRED') return '反馈服务正在部署，请稍后再试'
  return error?.message || '反馈提交失败，请稍后重试'
}

async function submitAiGuideFeedback({ category, description } = {}) {
  if (aiFeedbackStatus.value === 'submitting' || !config.taskId || !aiIntroduction.value) return
  const normalizedCategory = String(category || '').trim()
  const normalizedDescription = String(description || '').trim()
  const revision = readAiGuideRevision(aiGuideRevision.value)
  const signature = JSON.stringify([normalizedCategory, normalizedDescription, revision])
  if (!aiFeedbackClientRequestId || aiFeedbackRequestSignature !== signature) {
    aiFeedbackClientRequestId = createAiGuideFeedbackRequestId()
    aiFeedbackRequestSignature = signature
  }

  aiFeedbackRequestController?.abort()
  const requestController = new AbortController()
  aiFeedbackRequestController = requestController
  aiFeedbackStatus.value = 'submitting'
  aiFeedbackError.value = ''
  try {
    await modelGuideApi.submitFeedback(config.taskId, {
      category: normalizedCategory,
      description: normalizedDescription,
      guideRevision: revision,
      clientRequestId: aiFeedbackClientRequestId,
      shareId: config.shareId,
      activity: config.activity,
      channel: config.channel,
      signal: requestController.signal
    })
    aiFeedbackStatus.value = 'success'
    showToast('反馈已提交，感谢你的帮助')
  } catch (error) {
    if (error?.name === 'AbortError') return
    aiFeedbackStatus.value = 'error'
    aiFeedbackError.value = aiGuideFeedbackErrorMessage(error)
  } finally {
    if (aiFeedbackRequestController === requestController) aiFeedbackRequestController = null
  }
}

async function generateAiIntroduction({ open = true } = {}) {
  if (aiBusy.value || !config.taskId) return
  window.clearTimeout(aiAutoTimer)
  aiAutoTimer = 0
  aiAutoRequested = true
  if (open) openPanel('ai')
  aiStatus.value = 'preparing'
  aiIntroduction.value = ''
  aiError.value = ''
  aiGuideRevision.value = null
  resetAiGuideFeedback()
  aiRequestController?.abort()
  const requestController = new AbortController()
  aiRequestController = requestController

  try {
    const screenshots = await controller?.captureAiViews({ maxImages: 3, maxEdge: 960, quality: 0.78 }) || []
    if (!screenshots.length) throw new Error('暂时无法读取当前模型画面，请稍后重试')
    aiStatus.value = 'loading'
    const streamResult = await aiApi.stream({
      message: createAiIntroductionPrompt(),
      guideTaskId: config.taskId,
      images: screenshots,
      signal: requestController.signal,
      onDelta: content => { aiIntroduction.value += String(content || '') }
    })
    aiGuideRevision.value = readAiGuideRevision(
      streamResult?.meta?.revision ?? streamResult?.meta?.guide_revision ?? streamResult?.meta?.analysis_revision
    )
    try {
      const guide = await modelGuideApi.getModelGuide(config.taskId, {
        shareId: config.shareId,
        activity: config.activity,
        channel: config.channel,
        signal: requestController.signal
      })
      if (guide.status === 'completed') {
        aiIntroduction.value = guide.content
        aiGuideRevision.value = readAiGuideRevision(guide.revision)
      }
    } catch (error) {
      if (error?.name === 'AbortError') throw error
      console.warn('[MeTop viewer] AI guide revision unavailable', error)
    }
    aiStatus.value = 'success'
    aiError.value = ''
  } catch (error) {
    if (error?.name === 'AbortError') return
    aiIntroduction.value = ''
    const serviceMessage = getServicePausedMessage(error)
    aiError.value = serviceMessage || error?.message || 'AI 导游失败，请稍后重试'
    if (serviceMessage && config.embedded) showToast(serviceMessage)
    aiStatus.value = 'error'
  } finally {
    if (aiRequestController === requestController) aiRequestController = null
  }
}

function requestAiInsight() {
  markAiInsightSeen()
  openPanel('ai')
  if (['loading', 'success'].includes(aiStatus.value) || aiIntroduction.value) return
  generateAiIntroduction({ open: false })
}

function retryAiInsight() {
  if (aiBusy.value) return
  aiStatus.value = 'idle'
  aiError.value = ''
  generateAiIntroduction({ open: false })
}

function queueAutomaticAiIntroduction() {
  if (
    !loaded.value ||
    entrancePlaying.value ||
    aiAutoRequested ||
    !['idle', 'waiting'].includes(aiStatus.value) ||
    aiIntroduction.value
  ) return

  aiAutoRequested = true
  window.clearTimeout(aiAutoTimer)
  aiAutoTimer = window.setTimeout(() => {
    aiAutoTimer = 0
    if (!controller || !loaded.value || entrancePlaying.value) {
      aiAutoRequested = false
      queueAutomaticAiIntroduction()
      return
    }
    generateAiIntroduction({ open: false })
  }, 420)
}

function startTour() {
  closePanel()
  tourStepIndex.value = 0
  nextTick(() => {
    tourOpen.value = true
  })
}

function closeTour() {
  tourOpen.value = false
  tourStepIndex.value = 0
}

function readPhysicalLandscape() {
  physicalLandscape.value = window.innerWidth > window.innerHeight
}

function requestViewerFullscreen() {
  if (fullscreenElement()) return Promise.resolve(true)
  const request = viewerRoot.value?.requestFullscreen || viewerRoot.value?.webkitRequestFullscreen
  if (!request) return Promise.resolve(false)
  try {
    return Promise.resolve(request.call(viewerRoot.value))
      .then(() => {
        enteredFullscreen = fullscreenElement() === viewerRoot.value
        return true
      })
      .catch(() => false)
  } catch {
    return Promise.resolve(false)
  }
}

async function requestLandscapeOrientation() {
  if (!screen.orientation?.lock) return false
  try {
    await screen.orientation.lock('landscape')
    return true
  } catch {
    return false
  }
}

async function releaseViewerFullscreen() {
  if (!enteredFullscreen || !fullscreenElement()) return
  const exit = document.exitFullscreen || document.webkitExitFullscreen
  try {
    await Promise.resolve(exit?.call(document))
  } catch {
    // Exiting fullscreen can be rejected after the host WebView already closed it.
  }
  enteredFullscreen = false
}

async function enterLandscape() {
  if (!requireLoaded()) return
  const requestToken = ++orientationRequestToken
  closePanel()
  closeTour()
  landscapeMode.value = true
  enterFlyMode({ toast: false })
  readPhysicalLandscape()

  if (config.embedded) {
    showToast('横屏飞行：左侧摇杆移动 · 右侧摇杆转动视角')
    return
  }

  const fullscreenReady = await requestViewerFullscreen()
  if (requestToken !== orientationRequestToken || !landscapeMode.value) return
  const orientationLocked = await requestLandscapeOrientation()
  if (requestToken !== orientationRequestToken || !landscapeMode.value) return
  window.setTimeout(readPhysicalLandscape, 180)
  showToast(orientationLocked || physicalLandscape.value || fullscreenReady
    ? '横屏飞行：左侧摇杆移动 · 右侧摇杆转动视角'
    : '横屏飞行布局：左侧摇杆移动 · 右侧摇杆转动视角')
}

async function leaveLandscape({ toast = true } = {}) {
  orientationRequestToken += 1
  if (!landscapeMode.value && !enteredFullscreen) {
    if (toast) showToast('当前已是竖屏')
    return
  }
  landscapeMode.value = false
  exitFlyMode({ toast: false })
  try {
    screen.orientation?.unlock?.()
  } catch {
    // Some embedded browsers expose unlock but reject the call.
  }
  await releaseViewerFullscreen()
  readPhysicalLandscape()
  if (toast) showToast('已切换到竖屏')
}

function toggleOrientation() {
  if (landscapeMode.value) leaveLandscape()
  else enterLandscape()
}

function enterFlyMode({ toast = true, collapseRail = !landscapeMode.value } = {}) {
  if (!requireLoaded()) return
  closePanel()
  closeTour()
  // Landscape has enough horizontal space for the complete navigation rail,
  // so keep it visible by default. Portrait fly mode remains compact.
  railCollapsed.value = Boolean(collapseRail)
  controller?.setJoystickInput()
  cameraMode.value = controller?.setControlMode('fly') || 'fly'
  if (toast) showToast(`飞行模式：${flyControlHint.value}`)
}

function exitFlyMode({ toast = true } = {}) {
  controller?.setJoystickInput()
  cameraMode.value = controller?.setControlMode('orbit') || 'orbit'
  controller?.resetView({ toast: false })
  if (toast) showToast('已退出飞行模式')
}

function handleOrientationChange() {
  readPhysicalLandscape()
  window.setTimeout(readPhysicalLandscape, 160)
}

function handleFullscreenChange() {
  const current = fullscreenElement()
  if (current === viewerRoot.value) enteredFullscreen = true
  else if (enteredFullscreen) {
    enteredFullscreen = false
    const shouldRestorePortraitView = landscapeMode.value
    landscapeMode.value = false
    orientationRequestToken += 1
    if (shouldRestorePortraitView) {
      exitFlyMode({ toast: false })
    }
  }
  readPhysicalLandscape()
}

function updateJoysticks(input) {
  if (cameraMode.value === 'fly') controller?.setJoystickInput(input)
}

function requireLoaded() {
  if (loaded.value) return true
  showToast('模型仍在加载')
  return false
}

function handleTool(action) {
  if (action === 'help') {
    startTour()
    return
  }
  if (action === 'info') {
    openPanel('info')
    return
  }
  if (!requireLoaded()) return
  if (action === 'videoRecord') {
    void controller?.startVideoRecording()
    return
  }
  if (action === 'imageVideo') {
    if (!config.authToken) {
      openLoginPrompt('imageVideo')
      return
    }
    void controller?.startImageVideoGeneration()
    return
  }
  if (action === 'annotation') {
    if (!canEditAnnotations.value) return
    if (annotationEditing.value) requestFinishAnnotationEdit()
    else beginAnnotationEdit()
    return
  }
  if (action === 'ai') {
    requestAiInsight()
    return
  }
  if (action === 'ar') {
    controller?.openArProjection()
    return
  }
  if (action === 'cameraTrajectory') {
    controller?.toggleCameraTrajectory()
    return
  }
  if (action === 'like') requestLike()
  if (action === 'adjust') openPanel('adjust')
  if (action === 'reset') resetView()
  if (action === 'orientation') toggleOrientation()
  if (action === 'mode') {
    if (cameraMode.value === 'fly') exitFlyMode()
    else enterFlyMode()
  }
  if (action === 'share') controller?.shareModelPage()
}

function resetView() {
  if (!requireLoaded()) return
  controller?.resetView()
}

function captureSnapshot() {
  const image = controller?.captureSnapshot()
  if (!image) return
  snapshotImage.value = image
  openPanel('snapshot')
}

function updateFov(value) {
  settings.fov = value
  controller?.setFov(value)
}

function updateSpeed(value) {
  settings.speed = value
  controller?.setOrbitSpeed(value)
}

function updateMoveSpeed(value) {
  settings.moveSpeed = value
  controller?.setMoveSpeed(value)
}

function updatePinchSpeed(value) {
  settings.pinchSpeed = value
  controller?.setPinchSpeed(value)
}

function updateBackground(value) {
  settings.background = value
  controller?.setBackground(value)
}

function updateSkyboxEnabled(value) {
  settings.skyboxEnabled = Boolean(value)
  controller?.setSkyboxEnabled(settings.skyboxEnabled)
}

function updateSkyboxPreset(value) {
  settings.skyboxPreset = value
  settings.skyboxEnabled = true
  controller?.setSkyboxPreset(value)
}

async function saveInitialView() {
  if (!isProject.value || initialViewSaving.value) return
  if (!requireLoaded()) return
  initialViewSaving.value = true
  try {
    const savedPose = await controller?.saveInitialCameraPose()
    if (!savedPose) return
    settings.initialCameraPose = savedPose
    showToast('当前视角已设为初始视角')
  } catch (error) {
    showToast(error?.message || '初始视角保存失败')
  } finally {
    initialViewSaving.value = false
  }
}

function showProjectAction(label) {
  closePanel()
  showToast(`${label}入口已保留`)
}

function handleDebugPanel() {
  const allowed = ['info', 'ai', 'adjust', 'views', 'result', 'projectActions', 'help', 'annotation']
  if (!allowed.includes(config.debugPanel)) {
    if (isProject.value && config.debugUi) panel.value = 'result'
    return
  }
  if (config.debugPanel === 'help') startTour()
  else if (config.debugPanel === 'annotation') window.setTimeout(beginAnnotationEdit, 40)
  else panel.value = config.debugPanel
}

watch(tourOpen, active => controller?.setTourActive(active))
watch(isSoftwareLandscape, active => controller?.setSoftwareLandscape(active))
watch([loaded, entrancePlaying], queueAutomaticAiIntroduction, { flush: 'post' })

onMounted(() => {
  detachSheetScroll = attachSoftwareLandscapeSheetScroll(viewerRoot.value, () => isSoftwareLandscape.value)
  document.title = `${config.fallbackTitle} · MeTop.ai`
  controller = new ViewerRuntime({
    root: viewerRoot.value,
    canvas: canvas.value,
    config,
    callbacks: {
      onLoading: updateLoading,
      onMeta: updateMeta,
      onSettings: updateSettings,
      onLoaded: value => { loaded.value = value },
      onCameraTrajectory: state => {
        cameraTrajectoryAvailable.value = Boolean(state?.available)
        cameraTrajectoryPlaying.value = Boolean(state?.playing)
      },
      onVideoRecording: state => {
        Object.assign(videoRecording, state || {})
      },
      onImageVideo: state => {
        Object.assign(imageVideo, state || {})
      },
      onEntrance: value => { entrancePlaying.value = value },
      onAnnotations: updateAnnotations,
      onAnnotationCreate: handleAnnotationCreate,
      onAnnotationSelect: handleAnnotationSelect,
      onAnnotationMenuPosition: handleAnnotationMenuPosition,
      onAnnotationAttachmentPreview: handleAnnotationAttachmentPreview,
      onToast: showToast,
      onAuthRequired: detail => { openLoginPrompt(detail?.kind || 'like') }
    }
  })
  handleDebugPanel()
  readPhysicalLandscape()
  window.addEventListener('resize', handleOrientationChange)
  window.addEventListener('orientationchange', handleOrientationChange)
  document.addEventListener('fullscreenchange', handleFullscreenChange)
  document.addEventListener('webkitfullscreenchange', handleFullscreenChange)
  controller.load()
  queueAutomaticAiIntroduction()
})

onBeforeUnmount(() => {
  detachSheetScroll?.()
  detachSheetScroll = null
  orientationRequestToken += 1
  window.clearTimeout(toastTimer)
  window.clearTimeout(aiAutoTimer)
  aiRequestController?.abort()
  aiFeedbackRequestController?.abort()
  window.removeEventListener('resize', handleOrientationChange)
  window.removeEventListener('orientationchange', handleOrientationChange)
  document.removeEventListener('fullscreenchange', handleFullscreenChange)
  document.removeEventListener('webkitfullscreenchange', handleFullscreenChange)
  try {
    screen.orientation?.unlock?.()
  } catch {
    // No cleanup is required when the embedded browser owns orientation.
  }
  controller?.destroy()
  controller = null
})
</script>

<template>
  <main ref="viewerRoot" class="viewer" :class="rootClasses">
    <canvas ref="canvas" class="viewer__canvas" data-tour="canvas" aria-label="3D 空间模型"></canvas>
    <div class="viewer__gesture-target" data-tour="canvas-gesture" aria-hidden="true"></div>

    <LoadingState :state="loading" @retry="controller?.load()" @exit="exitViewer" />

    <LoginPromptDialog
      :open="likeLoginPromptOpen"
      :kind="loginPromptKind"
      @cancel="likeLoginPromptOpen = false"
      @confirm="goToLogin"
    />

    <button
      v-if="showMiniProgramHome"
      class="viewer-home-button"
      type="button"
      aria-label="返回小程序首页"
      @click="goMiniProgramHome"
    >
      <ViewerIcon name="home" />
      <span>首页</span>
    </button>

    <ToolRail
      v-show="!annotationEditing"
      :collapsed="railCollapsed"
      :liked="meta.isLiked"
      :likes="meta.likes"
      :camera-mode="cameraMode"
      :camera-trajectory-available="cameraTrajectoryAvailable"
      :camera-trajectory-playing="cameraTrajectoryPlaying"
      :video-record-available="VIDEO_RECORDING_ENTRY_VISIBLE && videoRecording.available"
      :video-recording="videoRecording.status === 'recording' || videoRecording.status === 'processing'"
      :image-video-generating="!['idle', 'ready', 'error'].includes(imageVideo.taskStatus)"
      :image-video-status="imageVideo.taskStatus"
      :landscape-mode="landscapeMode"
      :ai-loading="aiBusy"
      :ai-status="aiStatus"
      :ai-notice-visible="aiNoticeVisible"
      :notice-active="toastVisible"
      :gesture-notice-active="gestureNoticeActive"
      :annotation-editing="annotationEditing"
      :annotation-editable="canEditAnnotations"
      :ar-available="canProjectAr"
      :tour-expanded="tourOpen && tourStepIndex > 0"
      @update:collapsed="updateRailCollapsed"
      @action="handleTool"
    />

    <div
      v-if="meta.title && !annotationEditing"
      class="viewer-info"
    >
      <button
        class="viewer-info__identity"
        type="button"
        aria-label="查看模型信息"
        @click="openPanel('info')"
      >
        <span class="viewer-info__avatar" aria-hidden="true">
          <img
            v-if="meta.avatar && !infoAvatarFailed"
            :src="meta.avatar"
            alt=""
            @error="infoAvatarFailed = true"
          />
          <img v-else class="viewer-info__avatar-default" :src="companyAvatar" alt="" />
        </span>
        <span class="viewer-info__author">@{{ meta.author || 'MeTop 用户' }}</span>
        <span class="viewer-info__separator" aria-hidden="true">·</span>
        <strong class="viewer-info__title">{{ meta.title }}</strong>
      </button>
      <button
        v-if="meta.description"
        class="viewer-info__description-toggle"
        :class="{ 'is-expanded': descriptionExpanded }"
        type="button"
        :aria-expanded="descriptionExpanded"
        @click="descriptionExpanded = !descriptionExpanded"
      >
        <span class="viewer-info__description">{{ meta.description }}</span>
        <span class="viewer-info__description-action">{{ descriptionExpanded ? '收起' : '展开' }}</span>
      </button>
    </div>

    <AnnotationOverlay
      :annotations="annotations"
      :current-index="annotationCurrentIndex"
      :visible="annotationVisible"
      :editing="annotationEditing"
      :can-edit="canEditAnnotations"
      :expanded="annotationExpanded"
      :callout="annotationCallout"
      :saving="annotationSaving"
      :save-progress="annotationSaveProgress"
      :upload-items="annotationUploadItems"
      :menu="annotationMenu"
      @previous="stepAnnotation(-1)"
      @next="stepAnnotation(1)"
      @toggle-visible="toggleAnnotationVisible"
      @toggle-expanded="toggleAnnotationExpanded"
      @toggle-style="toggleAnnotationStyle"
      @edit="editMenuAnnotation"
      @snapshot="snapshotMenuAnnotation"
      @delete="deleteMenuAnnotation"
      @finish-edit="requestFinishAnnotationEdit"
    />
    <div v-if="annotationSnapshotFlash" class="annotation-snapshot-flash"></div>

    <VirtualJoysticks
      :active="cameraMode === 'fly' && loaded && !entrancePlaying && videoRecording.status === 'idle'"
      :software-rotated="isSoftwareLandscape"
      :dual="useDualJoysticks"
      @change="updateJoysticks"
    />

    <VideoRecordingPanel
      :status="videoRecording.status"
      :progress="videoRecording.progress"
      :url="videoRecording.url"
      :mode="videoRecording.mode"
      :saving="videoRecording.saving"
      :save-progress="videoRecording.saveProgress"
      :saving-action="videoRecording.savingAction"
      :software-rotated="isSoftwareLandscape"
      @cancel="controller?.cancelVideoRecording()"
      @close="controller?.closeRecordedVideo()"
      @save="controller?.saveRecordedVideo()"
      @share="controller?.shareRecordedVideo()"
    />

    <VideoRecordingPanel
      :status="imageVideo.status"
      :url="imageVideo.url"
      :saving="imageVideo.saving"
      :saving-action="imageVideo.savingAction"
      :stage-text="imageVideo.stageText"
      :task-id="imageVideo.taskId"
      :notification-available="config.videoSubscribeEnabled"
      :error="imageVideo.error"
      :retryable="imageVideo.retryable"
      :resume-only="imageVideo.resumeOnly"
      :can-pause="imageVideo.canPause"
      :software-rotated="isSoftwareLandscape"
      generated
      @close="controller?.closeGeneratedImageVideo()"
      @notify="controller?.openImageVideoNotification()"
      @retry="controller?.startImageVideoGeneration()"
      @save="controller?.saveGeneratedImageVideo()"
      @share="controller?.shareGeneratedImageVideo()"
    />

    <div class="toast" :class="{ show: toastVisible }" role="status">{{ toast }}</div>
    <div v-if="overlayVisible" class="overlay" @click="closePanel"></div>

    <AnnotationDialog
      :open="annotationDialogOpen"
      :mode="annotationDialogMode"
      :annotation="annotationDialogItem"
      @close="closeAnnotationDialog"
      @save="saveAnnotationDialog"
      @delete="deleteDialogAnnotation"
      @error="showToast"
    />
    <AnnotationMediaPreview
      :attachment="annotationPreviewAttachment"
      :kind="annotationPreviewKind"
      :media-layout="annotationPreviewLayout"
      @close="closeAnnotationAttachmentPreview"
    />
    <AnnotationExitDialog
      :open="annotationExitOpen"
      :saving="annotationSaving"
      @cancel="annotationExitOpen = false"
      @discard="discardAnnotationEdit"
      @save="saveAnnotationEdit"
    />
    <InfoSheet
      :open="panel === 'info'"
      :meta="meta"
      @close="closePanel"
      @like="requestLike"
    />
    <AiInsightSheet
      :open="panel === 'ai'"
      :content="aiIntroduction"
      :status="aiStatus"
      :error="aiError"
      :feedback-status="aiFeedbackStatus"
      :feedback-error="aiFeedbackError"
      @close="closePanel"
      @retry="retryAiInsight"
      @submit-feedback="submitAiGuideFeedback"
      @reset-feedback="resetAiGuideFeedback"
    />
    <AdjustSheet
      :open="panel === 'adjust'"
      :fov="settings.fov"
      :speed="settings.speed"
      :move-speed="settings.moveSpeed"
      :pinch-speed="settings.pinchSpeed"
      :background="settings.background"
      :skybox-enabled="settings.skyboxEnabled"
      :skybox-preset="settings.skyboxPreset"
      :can-set-initial-view="isProject"
      :initial-view-saving="initialViewSaving"
      @close="closePanel"
      @update:fov="updateFov"
      @update:speed="updateSpeed"
      @update:move-speed="updateMoveSpeed"
      @update:pinch-speed="updatePinchSpeed"
      @update:background="updateBackground"
      @update:skybox-enabled="updateSkyboxEnabled"
      @update:skybox-preset="updateSkyboxPreset"
      @reset-view="resetView"
      @set-initial-view="saveInitialView"
    />
    <SnapshotDialog
      :open="panel === 'snapshot'"
      :image="snapshotImage"
      @close="closePanel"
      @share="controller?.shareSnapshot(snapshotImage)"
    />
    <ProjectResultPanel
      :open="panel === 'result'"
      :title="compactTitle(meta.title, 10)"
      @close="closePanel"
      @download="captureSnapshot"
      @share="controller?.shareModelPage()"
      @actions="openPanel('projectActions')"
    />

    <section class="sheet sheet--actions" :class="{ 'is-open': panel === 'projectActions' }" :aria-hidden="panel !== 'projectActions'">
      <button class="sheet__handle" type="button" aria-label="收起项目操作" @click="closePanel">
        <ViewerIcon name="chevron-down" />
      </button>
      <div class="project-actions__list">
        <button type="button" @click="openPanel('info')">项目信息</button>
        <button type="button" @click="showProjectAction('重命名')">重命名</button>
        <button type="button" @click="showProjectAction('创建副本')">创建副本</button>
        <button class="project-actions__danger" type="button" @click="showProjectAction('删除')">删除</button>
      </div>
    </section>

    <CoachTour
      :open="tourOpen"
      :steps="tourSteps"
      :root="viewerRoot"
      :software-rotated="isSoftwareLandscape"
      @close="closeTour"
      @step-change="tourStepIndex = $event"
    />
  </main>
</template>
